import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import AreaAnalysis from '../components/dashboard/AreaAnalysis';
import QualityWidget from '../components/dashboard/QualityWidget';
import ExportPanel from '../components/dashboard/ExportPanel';
import KpiRing from '../components/dashboard/KpiRing';
import { useAuth } from '../auth';
import { useDashboardSelection } from '../dashboardSelection';

/* Dashboard EVO — cruscotto analitico + selezione aree per export. */
export default function DashboardEvoPage() {
  const { can } = useAuth();
  const [kpi, setKpi] = useState(null);
  const [province, setProvince] = useState({});
  const [drillRegion, setDrillRegion] = useState(null);   // regione "aperta" a destra
  const [priorityKeys, setPriorityKeys] = useState(new Set());   // 'tipo:nome' delle aree con obiettivo attivo
  const [lockKeys, setLockKeys] = useState(new Set());           // 'tipo:nome' delle aree escluse dal 456
  const { selRegions, setSelRegions, selProvinces, setSelProvinces } = useDashboardSelection();
  // Schermo piccolo/laptop: KPI compatti (anello con totale+% dentro) per stare in UNA sola riga.
  // La soglia coincide con la media query CSS della dashboard.
  const [compact, setCompact] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 1550);
  useEffect(() => {
    const on = () => setCompact(window.innerWidth <= 1550);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);

  const loadBase = useCallback(async () => {
    try {
      const [k, p] = await Promise.allSettled([
        axios.get(`${API}/dashboard/kpi`),
        axios.get(`${API}/dashboard/province-geo`),
      ]);
      if (k.status === 'fulfilled') setKpi(k.value.data);
      if (p.status === 'fulfilled') setProvince(p.value.data.province || {});
    } catch { /* ignore */ }
  }, []);

  const loadObiettivi = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/obiettivi`);
      setPriorityKeys(new Set((r.data || []).filter((o) => o.stato === 'attivo').map((o) => `${o.tipo}:${o.nome}`)));
    } catch { /* ignore */ }
  }, []);

  const loadEsclusioni = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/settings`);
      const ze = r.data?.zone_escluse_456 || r.data?.zone_escluse || [];
      setLockKeys(new Set(ze.filter((z) => z?.nome).map((z) => `${z.tipo}:${z.nome}`)));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadBase(); loadObiettivi(); loadEsclusioni();
    const id = setInterval(() => { loadBase(); loadObiettivi(); loadEsclusioni(); }, 30000);
    return () => clearInterval(id);
  }, [loadBase, loadObiettivi, loadEsclusioni]);

  const kpiTot = kpi?.total || 0;         // Caricati (base per Controllati/Pronti/Esportati)
  const kpiChk = kpi?.verificati || 0;    // Controllati (base per TIM/NON TIM: sono gli analizzati)
  const pctOf = (v, base) => base ? Math.round(((v || 0) / base) * 100) : 0;
  const kpiCards = kpi ? [
    { label: 'Caricati', value: kpi.total, pct: 100, sub: `${(kpi.pending ?? 0).toLocaleString('it-IT')} in attesa`, color: '#38bdf8' },
    { label: 'Controllati', value: kpi.verificati, pct: pctOf(kpi.verificati, kpiTot), sub: `+${(kpi.incremento_oggi ?? 0).toLocaleString('it-IT')} oggi · sui caricati`, color: 'var(--accent)' },
    { label: 'TIM', value: kpi.tim, pct: pctOf(kpi.tim, kpiChk), sub: 'sui controllati', color: '#3b82f6' },
    { label: 'NON TIM', value: kpi.non_tim, pct: pctOf(kpi.non_tim, kpiChk), sub: 'sui controllati', color: '#ef4444' },
    { label: 'Pronti Export', value: kpi.esportabili, pct: pctOf(kpi.esportabili, kpiTot), sub: `${(kpi.esportabili_nuovi ?? 0).toLocaleString('it-IT')} nuovi · ${(kpi.esportabili_riproposti ?? 0).toLocaleString('it-IT')} scaduti`, color: '#22c55e' },
    { label: 'Esportati', value: kpi.esportati, pct: pctOf(kpi.esportati, kpiTot), sub: 'sui caricati', color: '#f97316' },
  ] : [];

  const provRows = Object.entries(province).map(([nome, d]) => {
    const tim = d.tim || 0, non_tim = d.non_tim_totale || 0, pending = d.pending || 0;
    return {
      nome, regione: d.regione || '—', tim, non_tim, pending,
      esportabili: d.esportabili || 0, esportati: d.esportati || 0,
      pronti_nuovi: d.pronti_nuovi || 0, pronti_riproposti: d.pronti_riproposti || 0,
      controllati: tim + non_tim, tot: tim + non_tim + pending,
    };
  });

  const ACC = ['tim', 'non_tim', 'pending', 'esportabili', 'esportati', 'pronti_nuovi', 'pronti_riproposti', 'controllati', 'tot'];
  const regMap = {};
  provRows.forEach(r => {
    const reg = r.regione || '—';
    if (!regMap[reg]) { regMap[reg] = { nome: reg }; ACC.forEach(k => regMap[reg][k] = 0); }
    ACC.forEach(k => { regMap[reg][k] += r[k]; });
  });
  const regRows = Object.values(regMap);

  const provShown = drillRegion ? provRows.filter(r => r.regione === drillRegion) : provRows;
  // Nelle tabelle mostra solo aree con almeno 1 numero pronto all'export
  const regRowsReady = regRows.filter(r => (r.esportabili || 0) > 0);
  const provReady = provShown.filter(r => (r.esportabili || 0) > 0);

  // toggle generico per i Set di selezione
  const makeToggle = (setFn) => (nome, names, state) => {
    setFn(prev => {
      const next = new Set(prev);
      if (nome === '__all__') {
        (names || []).forEach(n => state ? next.add(n) : next.delete(n));
      } else if (next.has(nome)) next.delete(nome);
      else next.add(nome);
      return next;
    });
  };
  const toggleRegion = makeToggle(setSelRegions);
  const toggleProvince = makeToggle(setSelProvinces);

  const clearSel = () => { setSelRegions(new Set()); setSelProvinces(new Set()); };

  // Manda una singola area (regione o provincia) in priorità all'AI 456 (verifica completa).
  const sendPriority = useCallback(async (tipo, nome) => {
    try {
      const r = await axios.post(`${API}/obiettivi/verifica-completa`, { aree: [{ tipo, nome }] });
      const { creati, saltati, limite_raggiunto } = r.data || {};
      if (creati) toast.success(`${nome} in priorità 456 (verifica completa)`);
      else if (limite_raggiunto) toast.error('Limite priorità 456 raggiunto (= linee attive). Rimuovi un obiettivo raggiunto.');
      else if (saltati && saltati.length) toast(`${nome} è già in coda`);
      loadObiettivi();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  }, [loadObiettivi]);

  const regPrio = new Set([...priorityKeys].filter((k) => k.startsWith('regione:')).map((k) => k.slice(8)));
  const provPrio = new Set([...priorityKeys].filter((k) => k.startsWith('provincia:')).map((k) => k.slice(10)));

  // Esclusioni 456: regioni escluse + province escluse (esplicite o perché la loro regione è esclusa).
  const regLock = new Set([...lockKeys].filter((k) => k.startsWith('regione:')).map((k) => k.slice(8)));
  const provLockExpl = new Set([...lockKeys].filter((k) => k.startsWith('provincia:')).map((k) => k.slice(10)));
  const provLock = new Set(provRows.filter((r) => regLock.has(r.regione) || provLockExpl.has(r.nome)).map((r) => r.nome));

  const backBtn = drillRegion ? (
    <button className="evo-btn-sm" style={{ background: 'var(--accent-dark, #b5480a)' }} onClick={() => setDrillRegion(null)}>← Tutte</button>
  ) : null;

  return (
    <div className="evo">
      <div className="evo-grid evo-grid-4">
        {/* KPI ROW */}
        {can('dash_kpi') && (
          <div className="evo-kpis" style={compact
            ? { display: 'flex', gap: 12, flexWrap: 'nowrap' }
            : { gridTemplateColumns: 'repeat(auto-fit, minmax(226px, 1fr))' }}>
            {kpi ? kpiCards.map((c, i) => (compact ? (
              <div key={i} className="kpi-card-in" style={{ animationDelay: `${i * 55}ms`, flex: '1 1 0', minWidth: 0, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 15, padding: '12px 6px', boxShadow: '0 8px 22px rgba(0,0,0,.08)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7 }}>
                <KpiRing pct={c.pct} color={c.color} size={82} stroke={8} center={
                  <div style={{ display: 'grid', placeItems: 'center', lineHeight: 1.05 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{(c.value ?? 0).toLocaleString('it-IT')}</span>
                    <span style={{ fontSize: 10, fontWeight: 800, color: c.color }}>{Math.round(c.pct || 0)}%</span>
                  </div>
                } />
                <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--muted)', textAlign: 'center', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.label}>{c.label}</div>
              </div>
            ) : (
              <div key={i} className="kpi-card-in" style={{ animationDelay: `${i * 55}ms`, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 15, padding: '14px 16px', boxShadow: '0 8px 22px rgba(0,0,0,.08)', display: 'flex', alignItems: 'center', gap: 14 }}>
                <KpiRing pct={c.pct} color={c.color} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)' }}>{c.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.02em', lineHeight: 1.15, fontVariantNumeric: 'tabular-nums', color: 'var(--text)', margin: '1px 0' }}>{(c.value ?? 0).toLocaleString('it-IT')}</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', lineHeight: 1.35 }}>{c.sub}</div>
                </div>
              </div>
            ))) : Array.from({ length: 6 }).map((_, i) => (
              <div key={i} style={{ ...(compact ? { flex: '1 1 0', minWidth: 0 } : {}), background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 15, padding: '14px 16px', boxShadow: '0 8px 22px rgba(0,0,0,.08)', display: 'flex', alignItems: 'center', gap: 14 }}>
                <div className="kpi-skel-el" style={{ width: 60, height: 60, borderRadius: '50%', flex: '0 0 auto' }} />
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div className="kpi-skel-el" style={{ width: '55%', height: 9 }} />
                  <div className="kpi-skel-el" style={{ width: '72%', height: 18 }} />
                  <div className="kpi-skel-el" style={{ width: '85%', height: 9 }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ANALISI collegate: regione -> province (con selezione) */}
        {can('dash_analisi') && (
          <div className="evo-analysis">
            <AreaAnalysis
              title="Analisi per Regione"
              areaLabel="Regione · clic = province"
              rows={regRowsReady}
              onRowClick={(nome) => setDrillRegion(prev => prev === nome ? null : nome)}
              selected={drillRegion}
              checkedSet={selRegions}
              onToggleCheck={toggleRegion}
              onPriority={(nome) => sendPriority('regione', nome)}
              priorityNames={regPrio}
              lockedNames={regLock}
            />
            <AreaAnalysis
              title={drillRegion ? `Province — ${drillRegion}` : 'Analisi per Provincia (tutte)'}
              areaLabel="Provincia"
              rows={provReady}
              action={backBtn}
              checkedSet={selProvinces}
              onToggleCheck={toggleProvince}
              onPriority={(nome) => sendPriority('provincia', nome)}
              priorityNames={provPrio}
              lockedNames={provLock}
            />
          </div>
        )}

        {/* BONTÀ (metà) + ESPORTAZIONE (metà, sotto Provincia) */}
        {(can('dash_bonta') || can('dash_export')) && (
          <div className="evo-bottom">
            {can('dash_bonta') && <QualityWidget regRows={regRows} provRows={provRows} kpi={kpi} />}
            {can('dash_export') && (
              <ExportPanel
                selectedRegions={selRegions}
                selectedProvinces={selProvinces}
                provRows={provRows}
                onClear={clearSel}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
